import i18n from '@/i18n';

/** Locale tag for Intl, following the UI language. Greek uses "." for thousands and "," for decimals. */
export function intlLocale(lang = i18n.language): string {
  return lang?.startsWith('en') ? 'en-GB' : 'el-GR';
}

export function formatNumber(n: number, opts?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(intlLocale(), opts).format(n);
}

export function formatPercent(ratio: number, digits = 1) {
  return new Intl.NumberFormat(intlLocale(), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(ratio);
}

/** Money is always integer minor units (ARCHITECTURE §5). */
export function formatMoney(cents: number, currency = 'EUR', opts?: { compact?: boolean }) {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(intlLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: opts?.compact && whole ? 0 : 2,
    maximumFractionDigits: opts?.compact && whole ? 0 : 2,
  }).format(cents / 100);
}

export const DEFAULT_TIME_ZONE = 'Europe/Athens';
let displayTimeZone: string = DEFAULT_TIME_ZONE;

/**
 * The zone dates and times are shown in: the current tenant's timezone (the server schedules in
 * it too), set by TenantProvider; Europe/Athens until a tenant is loaded or for an invalid zone.
 */
export function setDisplayTimeZone(tz: string | null | undefined): void {
  let next = DEFAULT_TIME_ZONE;
  if (tz) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: tz });
      next = tz;
    } catch {
      /* unknown zone: keep the default */
    }
  }
  displayTimeZone = next;
}

export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

export function formatDate(
  value: string | number | Date,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
) {
  // 24-hour clock everywhere: matches the quiet-hours windows ("21:00–09:00") and avoids
  // "π.μ." at the end of a sentence turning into "π.μ..".
  return new Intl.DateTimeFormat(intlLocale(), {
    timeZone: displayTimeZone,
    hourCycle: 'h23',
    ...opts,
  }).format(new Date(value));
}

export function formatDateTime(value: string | number | Date) {
  return formatDate(value, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** "17:29" — 24-hour clock like every other time in the app (optionally in a given zone). */
export function formatTime(value: string | number | Date, timeZone?: string) {
  return formatDate(value, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

export function formatRelative(value: string | number | Date, now: Date = new Date()) {
  const diff = (new Date(value).getTime() - now.getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(intlLocale(), { numeric: 'auto' });
  for (const [unit, secs] of UNITS) {
    if (Math.abs(diff) >= secs || unit === 'second')
      return rtf.format(Math.round(diff / secs), unit);
  }
  return '';
}

/** Initials for avatars: "Νίκος Παπαδόπουλος" → "ΝΠ". */
export function initials(name: string | null | undefined, fallback = '·') {
  if (!name) return fallback;
  const parts = name
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  const letters = (parts.length > 1 ? [parts[0], parts[parts.length - 1]] : parts.slice(0, 1))
    .map((p) => p![0]!.toLocaleUpperCase('el-GR'))
    .join('');
  return letters || fallback;
}

/** "+30 690 000 1011" — the one phone format used on every screen (E.164 in, grouped out). */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  const gr = /^\+30(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (gr) return `+30 ${gr[1]} ${gr[2]} ${gr[3]}`;
  const cy = /^\+357(\d{2})(\d{6})$/.exec(e164);
  if (cy) return `+357 ${cy[1]} ${cy[2]}`;
  return e164;
}
